/**
 * 充值页面
 *
 * 第一方充值入口，位于作者中心。功能：
 * - 显示当前钱包余额
 * - 固定面额快捷充值（5/10/20/50/100 元）
 * - 自定义金额输入（0.01 ~ 10000 元）
 * - 立即充值 → 调用后端创建订单 → 跳转支付宝电脑网站支付
 * - 支付完成同步返回（URL 带 ?order_no=）→ 显示成功提示并刷新余额
 *
 * 当前迭代仅支持支付宝；微信支付将在后续迭代接入。
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthorCenterLayout } from '../components/layout/AuthorCenterLayout';
import { useAuthStore } from '../store/authStore';
import { paymentApi, OrderItem, PaymentApiError } from '../services/paymentApi';

// 固定充值面额（元）
const PRESET_AMOUNTS = [5, 10, 20, 50, 100];

// 充值金额范围
const MIN_AMOUNT = 0.01;
const MAX_AMOUNT = 10000;

// 订单状态文案映射
const STATUS_LABELS: Record<OrderItem['status'], string> = {
  pending: '待支付',
  paid: '已支付',
  failed: '已失败',
  closed: '已关闭',
};

// 订单状态样式映射
const STATUS_STYLES: Record<OrderItem['status'], string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  closed: 'bg-gray-100 text-gray-600',
};

export const RechargePage: React.FC = () => {
  const { isAuthenticated, initialized } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [balance, setBalance] = useState<string>('0.00');
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [selectedAmount, setSelectedAmount] = useState<number>(10);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 当前生效的金额（优先自定义金额，否则使用选中固定面额）
  const effectiveAmount = useMemo(() => {
    const trimmed = customAmount.trim();
    if (trimmed === '') return selectedAmount;
    const n = parseFloat(trimmed);
    return Number.isFinite(n) ? n : selectedAmount;
  }, [customAmount, selectedAmount]);

  const fetchBalance = useCallback(async () => {
    try {
      const data = await paymentApi.getBalance();
      setBalance(data.balance_yuan);
    } catch (err) {
      // 余额加载失败不阻塞页面
      console.error('加载余额失败', err);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await paymentApi.getOrders(10);
      setOrders(data.items);
    } catch (err) {
      console.error('加载订单列表失败', err);
    }
  }, []);

  // 初始化：加载余额 + 订单
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    (async () => {
      setLoading(true);
      await Promise.all([fetchBalance(), fetchOrders()]);
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [isAuthenticated, fetchBalance, fetchOrders]);

  // 处理支付宝同步返回（URL 带 order_no）
  useEffect(() => {
    const orderNo = searchParams.get('order_no');
    if (!orderNo) return;

    let active = true;
    (async () => {
      try {
        // 轮询订单状态（异步通知可能晚于同步返回到达）
        let polled: OrderItem | null = null;
        for (let i = 0; i < 6; i++) {
          try {
            polled = await paymentApi.getOrder(orderNo);
            if (polled.status === 'paid') break;
          } catch {
            // 订单可能尚未同步，继续重试
          }
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
        if (!active) return;

        if (polled && polled.status === 'paid') {
          setSuccess(`充值成功！已到账 ${polled.amount_yuan} 元`);
          await Promise.all([fetchBalance(), fetchOrders()]);
        } else {
          setSuccess('支付已完成，系统正在确认到账，请稍后刷新查看余额');
          await fetchOrders();
        }
      } catch (err) {
        if (!active) return;
        console.error('查询订单状态失败', err);
      } finally {
        if (active) {
          // 清理 URL 上的 order_no，避免刷新重复触发
          setSearchParams({}, { replace: true });
        }
      }
    })();

    return () => {
      active = false;
    };
    // 仅在首次挂载且 URL 含 order_no 时执行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePresetClick = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setError('');
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 允许空字符串和合法的小数
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setCustomAmount(value);
      setError('');
    }
  };

  const validateAmount = (): number | null => {
    const amount = effectiveAmount;
    if (!Number.isFinite(amount) || amount < MIN_AMOUNT) {
      setError(`最小充值金额为 ${MIN_AMOUNT} 元`);
      return null;
    }
    if (amount > MAX_AMOUNT) {
      setError(`最大充值金额为 ${MAX_AMOUNT} 元`);
      return null;
    }
    return Math.round(amount * 100) / 100;
  };

  const handleRecharge = async () => {
    setError('');
    setSuccess('');

    const amount = validateAmount();
    if (amount === null) return;

    setSubmitting(true);
    try {
      const result = await paymentApi.createRecharge(amount, 'alipay');
      // 整页跳转到支付宝电脑网站支付
      window.location.href = result.pay_url;
    } catch (err) {
      if (err instanceof PaymentApiError) {
        setError(err.message);
      } else {
        setError('发起充值失败，请稍后重试');
      }
      setSubmitting(false);
    }
  };

  // 等待认证初始化
  if (!initialized) {
    return (
      <AuthorCenterLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
        </div>
      </AuthorCenterLayout>
    );
  }

  // 未登录（ProtectedRoute 会跳转，此处兜底）
  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthorCenterLayout>
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* 标题 */}
          <h1 className="text-2xl font-bold text-gray-900 mb-6">账户充值</h1>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 flex justify-between items-start">
              <span>{error}</span>
              <button onClick={() => setError('')} className="ml-4 underline flex-shrink-0">
                关闭
              </button>
            </div>
          )}

          {/* 成功提示 */}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md text-green-700 flex justify-between items-start">
              <span>{success}</span>
              <button onClick={() => setSuccess('')} className="ml-4 underline flex-shrink-0">
                关闭
              </button>
            </div>
          )}

          {/* 余额卡片 */}
          <div className="mb-8 p-6 bg-gradient-to-br from-ink-800 to-ink-700 rounded-2xl text-white">
            <div className="text-sm opacity-80 mb-2">钱包余额</div>
            {loading ? (
              <div className="text-3xl font-bold">加载中...</div>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">{balance}</span>
                <span className="text-lg opacity-80">元</span>
              </div>
            )}
            <button
              onClick={() => {
                fetchBalance();
                fetchOrders();
              }}
              className="mt-3 text-xs opacity-70 hover:opacity-100 underline"
            >
              刷新
            </button>
          </div>

          {/* 充值金额选择 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">选择充值金额</h2>

            {/* 固定面额 */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {PRESET_AMOUNTS.map((amount) => {
                const active = customAmount === '' && selectedAmount === amount;
                return (
                  <button
                    key={amount}
                    onClick={() => handlePresetClick(amount)}
                    className={`py-4 rounded-xl border-2 font-semibold transition-all ${
                      active
                        ? 'border-ink-800 bg-ink-50 text-ink-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-ink-300'
                    }`}
                  >
                    <span className="text-xl">{amount}</span>
                    <span className="text-sm ml-1">元</span>
                  </button>
                );
              })}
            </div>

            {/* 自定义金额 */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">自定义金额</label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  value={customAmount}
                  onChange={handleCustomChange}
                  placeholder={`输入金额（${MIN_AMOUNT} ~ ${MAX_AMOUNT} 元）`}
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-ink-800 focus:outline-none transition-colors"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">元</span>
              </div>
            </div>
          </div>

          {/* 支付方式 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">支付方式</h2>
            <div className="flex items-center gap-3 p-4 border-2 border-ink-800 rounded-xl bg-ink-50">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white text-sm font-bold">
                支
              </div>
              <span className="font-medium text-gray-900">支付宝</span>
              <span className="ml-auto text-xs text-gray-500">微信支付即将上线</span>
            </div>
          </div>

          {/* 立即充值 */}
          <div className="mb-8">
            <button
              onClick={handleRecharge}
              disabled={submitting}
              className={`w-full py-4 rounded-xl font-semibold text-white text-lg transition-all ${
                submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-ink-800 hover:bg-ink-700 hover:shadow-md'
              }`}
            >
              {submitting ? '正在创建订单...' : `立即充值 ${effectiveAmount.toFixed(2)} 元`}
            </button>
            <p className="mt-3 text-center text-xs text-gray-500">
              点击充值即表示同意充值条款，充值金额将计入账户余额
            </p>
          </div>

          {/* 最近订单 */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">最近订单</h2>
            {orders.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-xl">
                暂无充值记录
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium">订单号</th>
                      <th className="text-right py-3 px-4 font-medium">金额</th>
                      <th className="text-center py-3 px-4 font-medium">状态</th>
                      <th className="text-right py-3 px-4 font-medium">时间</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((order) => (
                      <tr key={order.order_no} className="hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-700 font-mono text-xs">
                          {order.order_no}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-900 font-medium">
                          {order.amount_yuan} 元
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              STATUS_STYLES[order.status]
                            }`}
                          >
                            {STATUS_LABELS[order.status]}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-500 text-xs">
                          {new Date(order.created_at).toLocaleString('zh-CN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthorCenterLayout>
  );
};

export default RechargePage;
